import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { fetchEvents } from '@components/Overview/Events/api';
import { useIsAdminChannelOptInEnabled } from '@hooks/useIsAdminChannelOptInEnabled';
import { UnreadMessages } from '@components/Overview/types';
import { ConversationsContext } from '@providers/ConversationsProvider/ConversationsProvider';
import { requestIdFromChannelId } from '@utils/chat';

interface ChannelOptedEvent { channels_opted?: boolean; id: number }

/**
 * Computes the nav-badge unread count filtered to only events the admin has opted into.
 *
 * Fetches opted-in status via TanStack Query keyed on the current set of unread channel IDs.
 * TanStack Query deduplicates by key (staleTime: Infinity), so re-fetches only happen when a
 * genuinely new channel ID appears in unreadChannelMap — covering events that had zero unread
 * at page load and later received a message.
 *
 * Note: opt-in changes mid-session (new opt-ins, collaborator additions) are not reflected
 * until the next time a message arrives for that event — accepted tradeoff.
 *
 * Call this hook wherever the nav badge lives (SideMenu) so it runs on every page.
 */
export const useFilteredUnreadBadgeCount = () => {
  const { setProperty, unreadChannelMap } = useContext(ConversationsContext);
  const isAdminChannelOptInEnabled = useIsAdminChannelOptInEnabled();

  // Derive event IDs from the current unread channels. Sorted for a stable query key
  // regardless of channel insertion order.
  const eventIds: UnreadMessages[] = Object.keys(unreadChannelMap)
    .map(requestIdFromChannelId)
    .filter((id): id is string => id != null)
    .map(id => ({ bid_request_id: id }));

  const { data: optedInEventIds, isError } = useQuery({
    enabled: isAdminChannelOptInEnabled && eventIds.length > 0,
    queryFn: ({ signal }) =>
      fetchEvents(
        { pagination: { current: 1, pageSize: eventIds.length } },
        { requestType: ['unreadMessages'] },
        eventIds,
        signal,
        true
      ).then(({ data }) => {
        const events: ChannelOptedEvent[] = data?.bid_requests ?? [];
        return new Set(events.filter(e => e.channels_opted).map(e => e.id.toString()));
      }),
    queryKey: ['filteredUnreadBadge', eventIds.map(e => e.bid_request_id).sort()],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: Infinity,
  });

  // Reactively recompute filteredUnreadCount whenever unreadChannelMap or optedInEventIds changes.
  useEffect(() => {
    if (!isAdminChannelOptInEnabled) return;

    const validEventIds = Object.keys(unreadChannelMap)
      .map(requestIdFromChannelId)
      .filter((id): id is string => id != null);

    if (validEventIds.length === 0) {
      setProperty({ filteredUnreadCount: 0 });
      return;
    }

    if (optedInEventIds === undefined) return;

    const filteredTotal = Object.entries(unreadChannelMap)
      .filter(([channelId]) => {
        const eventId = requestIdFromChannelId(channelId);
        return eventId != null && optedInEventIds.has(eventId);
      })
      .reduce((sum, [, count]) => sum + count, 0);

    setProperty({ filteredUnreadCount: filteredTotal });
  }, [isAdminChannelOptInEnabled, optedInEventIds, setProperty, unreadChannelMap]);

  return { isError };
};
